import { NextRequest, NextFetchEvent } from 'next/server';

import { formatInTimeZone } from 'date-fns-tz';
import { generateCookieDomain } from '../utils/cookies/generateCookieDomain';
import { generateCookieExpire } from '../utils/cookies/generateCookieExpire';

import { UTM_COOKIE_KEYS, UTM_QUERY_KEYS } from './constants';

import type { MiddlewareFactory } from './type';

interface CookieInfo {
  name: string;
  expires: Date;
  value?: string;
  isPreserve?: boolean;
}

interface QueryInfo {
  queryKey: string;
  queryValue: string;
}

const getCookiesBaseInfo =
  (cookieInfos: CookieInfo[]) =>
  (queryInfo: QueryInfo): Required<CookieInfo>[] => {
    return cookieInfos.map(({ name, expires, value, isPreserve }) => ({
      name,
      expires,
      value: value ?? queryInfo.queryValue,
      isPreserve: typeof isPreserve === 'boolean' ? isPreserve : false,
    }));
  };

type CookieMap = Record<
  keyof typeof UTM_QUERY_KEYS,
  {
    queryKey: keyof typeof UTM_QUERY_KEYS;
    getCookies: ReturnType<typeof getCookiesBaseInfo>;
  }
>;

const COOKIE_MAP: CookieMap = {
  [UTM_QUERY_KEYS.utm_medium]: {
    queryKey: UTM_QUERY_KEYS.utm_medium,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_utm_medium, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.utm_medium,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.utm_campaign]: {
    queryKey: UTM_QUERY_KEYS.utm_campaign,
    getCookies: getCookiesBaseInfo([
      {
        name: UTM_COOKIE_KEYS.recent_utm_campaign,
        expires: generateCookieExpire(7),
      },
      {
        name: UTM_COOKIE_KEYS.utm_campaign,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.utm_source]: {
    queryKey: UTM_QUERY_KEYS.utm_source,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_utm_source, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.recent_utm_date,
        expires: generateCookieExpire(7),
        value: formatInTimeZone(new Date(), 'GMT', 'yyyy-MM-dd HH:mm:ss'),
      },
      {
        name: UTM_COOKIE_KEYS.utm_source,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
      {
        name: UTM_COOKIE_KEYS.utm_date,
        expires: generateCookieExpire(1, 'months'),
        value: formatInTimeZone(new Date(), 'GMT', 'yyyy-MM-dd HH:mm:ss'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.utm_term]: {
    queryKey: UTM_QUERY_KEYS.utm_term,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_utm_term, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.utm_term,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.utm_content]: {
    queryKey: UTM_QUERY_KEYS.utm_content,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_utm_content, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.utm_content,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.contents_id]: {
    queryKey: UTM_QUERY_KEYS.contents_id,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.mrt_contents, expires: generateCookieExpire(1) },
    ]),
  },
  [UTM_QUERY_KEYS.n_ad_group]: {
    queryKey: UTM_QUERY_KEYS.n_ad_group,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_n_ad_group, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.n_ad_group,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.n_ad]: {
    queryKey: UTM_QUERY_KEYS.n_ad,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_n_ad, expires: generateCookieExpire(7) },
      { name: UTM_COOKIE_KEYS.n_ad, expires: generateCookieExpire(1, 'months'), isPreserve: true },
    ]),
  },
  [UTM_QUERY_KEYS.n_campaign_type]: {
    queryKey: UTM_QUERY_KEYS.n_campaign_type,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_n_campaign_type, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.n_campaign_type,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.n_keyword]: {
    queryKey: UTM_QUERY_KEYS.n_keyword,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_n_keyword, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.n_keyword,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
  [UTM_QUERY_KEYS.n_keyword_id]: {
    queryKey: UTM_QUERY_KEYS.n_keyword_id,
    getCookies: getCookiesBaseInfo([
      { name: UTM_COOKIE_KEYS.recent_n_keyword_id, expires: generateCookieExpire(7) },
      {
        name: UTM_COOKIE_KEYS.n_keyword_id,
        expires: generateCookieExpire(1, 'months'),
        isPreserve: true,
      },
    ]),
  },
};

export const utmMiddleware: MiddlewareFactory = (next, response) => {
  return (req: NextRequest, event: NextFetchEvent) => {
    const filterdQueryKeys = Object.values(UTM_QUERY_KEYS).filter((queryKey) => {
      const hasKey = req.nextUrl.searchParams.has(queryKey);
      if (!hasKey) return false;

      const queryValue = req.nextUrl.searchParams.get(queryKey);

      return queryValue != null && queryValue.trim() !== '';
    });

    filterdQueryKeys.forEach((queryKey) => {
      const queryValue = req.nextUrl.searchParams.get(queryKey) ?? '';
      const baseCookies = COOKIE_MAP[queryKey].getCookies({
        queryKey,
        queryValue,
      });

      baseCookies.forEach(({ name, expires, value, isPreserve }) => {
        const existingCookie = req.cookies.get(name);

        if (isPreserve && existingCookie?.value && existingCookie.value.trim() !== '') {
          return;
        }

        response.cookies.set(name, value, {
          expires,
          domain: generateCookieDomain(),
        });
      });
    });

    return next(req, event);
  };
};
